<template>
  <CollapsibleSection
    title="Proprietary Blends"
    :badge="store.blends.length || null"
    section-key="blends"
    @toggle="handleSectionToggle"
  >
    <!-- Custom title slot for logged-in mode with actions -->
    <template v-if="!store.isPublic" #title>
      <div class="section-title-with-actions">
        <span>Proprietary Blends</span>
        <div class="section-header-actions is-hidden-desktop">
          <ClearAllButton :show="store.blends.length > 0" clear-type="blends" variant="mobile" />
        </div>
      </div>
    </template>

    <!-- Public mode: show read-only message -->
    <template v-if="store.isPublic">
      <p class="has-text-grey is-size-7 mb-3">
        Proprietary blends cannot be edited in sample mode.
        <a :href="signUpPath({ continue_to: dashboardSupplementLabelsPath() })"
          >Create an account</a
        >
        to manage blends.
      </p>
      <div v-if="store.blends.length > 0" class="blends-list">
        <div v-for="blend in store.blends" :key="blend._uid" class="card mb-2 bg-light-gray">
          <div class="card-content py-2 px-3">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <strong><span v-html="formatBlendName(blend)"></span></strong>
            <span v-if="blend.amount" class="ml-2 has-text-grey">
              {{ blend.amount }} {{ blend.unit }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- Logged-in mode: full editing -->
    <template v-else>
      <p v-if="store.blends.length === 0" class="has-text-grey is-size-7 mb-3">
        No proprietary blends added. Use blends to group multiple ingredients under a single name
        with a combined weight.
      </p>

      <div class="blends-list-container" @focusin="handleFocusIn" @focusout="handleFocusOut">
        <div class="blends-list">
          <BlendItem
            v-for="(blend, index) in sortedBlends"
            :key="blend._uid"
            :uid="blend._uid"
            :data-blend-uid="blend._uid"
            :start-expanded="index === store.justAddedBlendIndex"
            :expanded="isExpanded(blend._uid)"
            :active="isActive(blend._uid)"
            :editing="isEditing(blend._uid)"
            @removed="handleFocusAfterRemove"
            @toggle="handleToggle(blend._uid)"
          />
        </div>
      </div>

      <div class="blend-add-wrapper is-hidden-desktop" data-sticky-footer>
        <div class="blend-add-inner field mt-3 mb-4">
          <button
            type="button"
            class="button is-small is-info is-outlined is-fullwidth"
            @click="handleAddBlend"
          >
            <span class="icon"><i class="fas fa-plus"></i></span>
            <span>Add Proprietary Blend</span>
          </button>
        </div>
      </div>

      <div class="buttons mb-0 mt-3 section-buttons is-hidden-touch">
        <button
          ref="addBlendBtn"
          type="button"
          class="button is-small is-info is-outlined section-add-btn"
          @click="handleAddBlend"
        >
          <span class="icon"><i class="fas fa-plus"></i></span>
          <span>Add Proprietary Blend</span>
        </button>
        <ClearAllButton :show="store.blends.length > 0" clear-type="blends" />
      </div>
    </template>
  </CollapsibleSection>
</template>

<script setup>
  import { ref, nextTick } from "vue";
  import { signUpPath, dashboardSupplementLabelsPath } from "~/routes";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import CollapsibleSection from "./collapsible-section.vue";
  import BlendItem from "./blend-item.vue";
  import ClearAllButton from "./clear-all-button.vue";
  import { useExpandCollapse } from "./composables/use-expand-collapse";
  import { useFocusAwareSort } from "./composables/use-focus-aware-sort";
  import { formatLabelName } from "./utils/formatting";
  import { sortByAmountDesc } from "./utils/sorting";
  import { focusAfterAdd } from "./utils/focus-helpers";

  const store = useSupplementLabelStore();
  const addBlendBtn = ref(null);

  function formatBlendName(blend) {
    return formatLabelName(blend.name, {
      italicizeParenthetical: blend.italicize_parenthetical,
      disableSubscripts: true,
    });
  }

  // Focus-aware sorting: pins the focused blend in place until user leaves the card
  // Use data-blend-uid to avoid collision with constituent's data-uid
  const {
    sortedItems: sortedBlends,
    handleFocusIn: handleFocusInSort,
    handleFocusOut: handleSortFocusOut,
  } = useFocusAwareSort({
    items: () => store.blends,
    sortFn: (items) => sortByAmountDesc(items, "amount", "unit"),
    dataAttr: "data-blend-uid",
  });

  function handleSectionToggle(isOpen, options = {}) {
    if (options.autoFocusFirstField && isOpen && store.blends.length === 0) {
      nextTick(() => {
        addBlendBtn.value?.focus();
      });
    }
  }

  function handleFocusAfterRemove(removedIndex) {
    nextTick(() => {
      const blends = document.querySelectorAll(".blends-list .card");
      if (blends[removedIndex]) {
        blends[removedIndex].querySelector(".blend-name-input")?.focus();
      } else if (blends[removedIndex - 1]) {
        blends[removedIndex - 1].querySelector(".blend-name-input")?.focus();
      } else {
        addBlendBtn.value?.focus();
      }
    });
  }

  function handleAddBlend() {
    store.addBlend();
    store.justAddedBlendIndex = store.blends.length - 1;
    focusAfterAdd(".blends-list .card .blend-name-input", () => store.clearJustAddedBlendIndex());
  }

  // Use composable for expand/collapse logic
  // Use data-blend-uid to avoid collision with constituent's data-uid
  const {
    isExpanded,
    isActive,
    isEditing,
    toggle: handleToggle,
    handleFocusIn: handleFocusInExpand,
    handleFocusOut: handleFocusOutExpand,
  } = useExpandCollapse({
    items: () => store.blends,
    justAddedIndex: () => store.justAddedBlendIndex,
    dataAttr: "data-blend-uid",
  });

  // Combined focus handlers
  function handleFocusIn(event) {
    handleFocusInExpand(event);
    handleFocusInSort(event);
  }

  function handleFocusOut(event) {
    handleFocusOutExpand(event);
    handleSortFocusOut(event);
  }
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";

  .blend-add-wrapper {
    @include sticky-mobile-footer(6);
  }

  .blend-add-inner {
    background: $white;
    padding-top: 0.35rem;
    margin: 0;
  }

  .blends-list {
    @include until($desktop) {
      padding-bottom: 0;
    }
  }

  .section-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    flex-direction: row-reverse;
  }

  .section-buttons .section-add-btn {
    flex: 2;
  }

  .section-buttons :deep(.section-clear-btn) {
    flex: 1;
  }
</style>
